import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import PageHero from "../components/site/PageHero";
import { IMAGES } from "../lib/images";
import { useReveal } from "../lib/useReveal";

export default function About() {
  const ref = useReveal();
  return (
    <div ref={ref}>
      <Seo
        title="About Us"
        path="/about"
        description="Mount Moriah Relief is a charitable organisation established in Northern Ireland in 2024, relieving poverty and hardship through humanitarian assistance and responsible partnerships."
      />
      <PageHero
        eyebrow="About Us"
        title="Compassion with accountability"
        image={IMAGES.northernIreland.src}
        imageAlt={IMAGES.northernIreland.alt}
      >
        <p>
          Mount Moriah Relief exists to prevent and relieve poverty, hardship and suffering —
          through humanitarian assistance, grants, essential goods, services and collaboration
          with other organisations.
        </p>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 reveal">
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-forest tracking-tight leading-snug">
              Who we are
            </h2>
            <div className="mt-6 space-y-5 text-charcoal/80 leading-relaxed">
              <p>
                Mount Moriah Relief is a charitable organisation established in Northern Ireland
                in 2024. We were founded on the belief that practical help — given thoughtfully
                and accountably — restores not only circumstances, but dignity.
              </p>
              <p>
                Our name comes from Mount Moriah, the place associated in the biblical account
                with God's provision: "On the mountain of the Lord it will be provided." That
                account of provision in a moment of great need inspires our mission today.
              </p>
              <p>
                From Northern Ireland, we direct our charitable work with a particular, but not
                exclusive, focus on supporting vulnerable communities in Zimbabwe. We work
                through collaboration — with local partners, other charities and community
                organisations — so that assistance is appropriate, effective and respectful.
              </p>
            </div>
          </div>
          <aside className="lg:col-span-5 reveal">
            <div className="bg-sandstone/60 border border-sandstone p-8">
              <h3 className="font-serif text-2xl font-semibold text-forest">At a glance</h3>
              <dl className="mt-6 space-y-5 text-sm">
                <div>
                  <dt className="text-xs tracking-wider uppercase font-semibold text-charcoal/60">Established</dt>
                  <dd className="mt-1 text-charcoal">2024, Northern Ireland</dd>
                </div>
                <div>
                  <dt className="text-xs tracking-wider uppercase font-semibold text-charcoal/60">Purpose</dt>
                  <dd className="mt-1 text-charcoal">The prevention and relief of poverty, hardship and suffering</dd>
                </div>
                <div>
                  <dt className="text-xs tracking-wider uppercase font-semibold text-charcoal/60">Focus</dt>
                  <dd className="mt-1 text-charcoal">Vulnerable communities in Zimbabwe, alongside need wherever we can responsibly help</dd>
                </div>
                <div>
                  <dt className="text-xs tracking-wider uppercase font-semibold text-charcoal/60">Contact</dt>
                  <dd className="mt-1">
                    <a href="mailto:admin@mountmoriahrelief.org" className="text-forest underline">admin@mountmoriahrelief.org</a>
                  </dd>
                </div>
              </dl>
            </div>
            <p className="mt-4 text-xs text-charcoal/60 leading-relaxed" data-testid="about-charity-note">
              Charity registration details will be published once formally confirmed. We do not
              describe ourselves as a registered charity until that status is independently verified.
            </p>
          </aside>
        </div>
      </section>

      <section className="py-24 bg-forest text-white">
        <div className="max-w-3xl mx-auto px-6 text-center reveal">
          <h2 className="font-serif text-3xl sm:text-4xl font-medium tracking-tight leading-snug">
            Governance you can examine
          </h2>
          <p className="mt-5 text-white/80 leading-relaxed">
            Trust is earned through openness. Read about our trustees, our governance
            arrangements and our commitment to financial accountability.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/trustees" className="border border-white/70 px-8 py-3 font-medium hover:bg-white hover:text-forest transition-colors duration-300" data-testid="about-trustees-link">
              Meet Our Trustees
            </Link>
            <Link to="/governance" className="bg-gold px-8 py-3 font-medium hover:bg-gold-dark transition-colors duration-300" data-testid="about-governance-link">
              Governance &amp; Transparency
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
